// src/components/Relatorios/Relatorios.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FiTrendingUp,
  FiUsers,
  FiDollarSign,
  FiTarget,
  FiActivity,
  FiMessageCircle,
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
  FiShoppingCart,
  FiPercent,
  FiTrendingDown,
} from "react-icons/fi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
} from "recharts";
import { useDashboardStore } from "../../services/useDashboardStore";
import useCrmStore from "../../services/useCrmStore";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";
import "./Relatorios.css";

const Relatorios = () => {
  const { dashboardData, loading: dashboardLoading, fetchDashboard } = useDashboardStore();
  const {
    stages,
    leads,
    fetchStages,
    fetchLeads,
    loading: crmLoading,
    salesMetrics,
    revenueEvolution,
    fetchSalesMetrics,
    fetchRevenueEvolution,
    salesLoading
  } = useCrmStore();
  const [period, setPeriod] = useState("30d");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchStages();
    fetchLeads();
    fetchSalesMetrics();
    fetchRevenueEvolution(30);
  }, [fetchDashboard, fetchStages, fetchLeads, fetchSalesMetrics, fetchRevenueEvolution]);

  // Refetch when period changes
  useEffect(() => {
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
    fetchRevenueEvolution(days);

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    fetchSalesMetrics(
      startDate.toISOString().split('T')[0],
      endDate.toISOString().split('T')[0]
    );
  }, [period, fetchRevenueEvolution, fetchSalesMetrics]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
    await Promise.all([
      fetchDashboard(),
      fetchStages(),
      fetchLeads(),
      fetchSalesMetrics(),
      fetchRevenueEvolution(days)
    ]);
    setIsRefreshing(false);
  };

  // Calcular métricas do CRM (fallback se não tiver salesMetrics)
  const calculateMetrics = () => {
    // Se temos salesMetrics do backend, usar esses dados
    if (salesMetrics) {
      return {
        totalLeads: salesMetrics.total_leads || 0,
        activeLeads: salesMetrics.total_leads - salesMetrics.converted_leads || 0,
        wonDeals: salesMetrics.total_sales_count || 0,
        totalRevenue: salesMetrics.total_sales_value || 0,
        conversionRate: salesMetrics.conversion_rate || 0,
        avgDealValue: salesMetrics.avg_ticket || 0,
        totalExpenses: salesMetrics.total_expenses || 0,
        profit: salesMetrics.profit || 0,
        convertedLeads: salesMetrics.converted_leads || 0,
      };
    }

    // Fallback para cálculo local
    if (!leads || leads.length === 0) {
      return {
        totalLeads: 0,
        activeLeads: 0,
        wonDeals: 0,
        totalRevenue: 0,
        conversionRate: 0,
        avgDealValue: 0,
        totalExpenses: 0,
        profit: 0,
        convertedLeads: 0,
      };
    }

    const wonStage = stages.find(s =>
      s.name?.toLowerCase().includes('ganho') ||
      s.name?.toLowerCase().includes('won') ||
      s.name?.toLowerCase().includes('fechado')
    );

    const wonLeads = wonStage
      ? leads.filter(l => l.stage_id === wonStage.id)
      : leads.filter(l => l.status === 'CONVERTED');

    const activeLeads = leads.filter(l => l.status === 'ACTIVE' || !l.status);

    const totalRevenue = wonLeads.reduce((sum, l) => sum + (parseFloat(l.deal_value) || 0), 0);
    const avgDealValue = wonLeads.length > 0 ? totalRevenue / wonLeads.length : 0;
    const conversionRate = leads.length > 0 ? (wonLeads.length / leads.length) * 100 : 0;

    return {
      totalLeads: leads.length,
      activeLeads: activeLeads.length,
      wonDeals: wonLeads.length,
      totalRevenue,
      conversionRate,
      avgDealValue,
      totalExpenses: 0,
      profit: totalRevenue,
      convertedLeads: wonLeads.length,
    };
  };

  // Dados do funil
  const getFunnelData = () => {
    if (!stages || !leads) return [];

    return stages
      .sort((a, b) => a.order - b.order)
      .map(stage => ({
        name: stage.name,
        leads: leads.filter(l => l.stage_id === stage.id).length,
        value: leads.filter(l => l.stage_id === stage.id)
          .reduce((sum, l) => sum + (parseFloat(l.deal_value) || 0), 0),
        color: stage.color || '#00bf63',
      }));
  };

  // Leads por fonte
  const getLeadsBySource = () => {
    if (!leads || leads.length === 0) return [];

    const sourceMap = {};
    leads.forEach(lead => {
      const source = lead.source || 'Direto';
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const colors = ['#00bf63', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    return Object.entries(sourceMap).map(([name, value], index) => ({
      name,
      value,
      color: colors[index % colors.length],
    }));
  };

  // Leads por período (simulado baseado em created_at)
  const getLeadsOverTime = () => {
    if (!leads || leads.length === 0) return [];

    const now = new Date();
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
    const periods = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

      const dayLeads = leads.filter(l => {
        if (!l.created_at) return false;
        const leadDate = new Date(l.created_at);
        return leadDate.toDateString() === date.toDateString();
      });

      periods.push({
        date: dateStr,
        novos: dayLeads.length,
        convertidos: dayLeads.filter(l => l.status === 'CONVERTED').length,
      });
    }

    // Se temos muitos dias, agregar por semana ou mês
    if (days > 30) {
      // Agregar por semana
      const aggregated = [];
      for (let i = 0; i < periods.length; i += 7) {
        const week = periods.slice(i, i + 7);
        aggregated.push({
          date: week[0]?.date || '',
          novos: week.reduce((sum, d) => sum + d.novos, 0),
          convertidos: week.reduce((sum, d) => sum + d.convertidos, 0),
        });
      }
      return aggregated;
    }

    return periods;
  };

  // Revenue evolution data for chart
  const getRevenueChartData = () => {
    if (!revenueEvolution || revenueEvolution.length === 0) return [];

    // Se temos muitos pontos, agregar
    if (revenueEvolution.length > 30) {
      const aggregated = [];
      for (let i = 0; i < revenueEvolution.length; i += 7) {
        const week = revenueEvolution.slice(i, i + 7);
        const lastItem = week[week.length - 1];
        aggregated.push({
          date: week[0]?.date_formatted || '',
          sales: week.reduce((sum, d) => sum + (d.sales || 0), 0),
          revenue: week.reduce((sum, d) => sum + (d.revenue || 0), 0),
          expenses: week.reduce((sum, d) => sum + (d.expenses || 0), 0),
          net: week.reduce((sum, d) => sum + (d.net || 0), 0),
          cumulative: lastItem?.cumulative || 0,
        });
      }
      return aggregated;
    }

    return revenueEvolution.map(item => ({
      date: item.date_formatted,
      sales: item.sales || 0,
      revenue: item.revenue || 0,
      expenses: item.expenses || 0,
      net: item.net || 0,
      cumulative: item.cumulative || 0,
    }));
  };

  const metrics = calculateMetrics();
  const funnelData = getFunnelData();
  const sourceData = getLeadsBySource();
  const timeData = getLeadsOverTime();
  const revenueChartData = getRevenueChartData();

  const loading = dashboardLoading || crmLoading;

  if (loading && !dashboardData && !leads.length) {
    return <FullScreenLoader message="Carregando Relatórios..." />;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="relatorios">
      {/* Header */}
      <motion.header
        className="relatorios-header"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div>
          <p className="relatorios-kicker">Relatórios</p>
          <h1 className="relatorios-title">Analytics de Vendas</h1>
          <p className="relatorios-subtitle">
            Acompanhe suas métricas de vendas, conversões e desempenho do funil em tempo real.
          </p>
        </div>
        <div className="relatorios-header-actions">
          <select
            className="relatorios-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
          </select>
          <button
            className="relatorios-btn-secondary"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <FiRefreshCw className={isRefreshing ? "spinning" : ""} />
            Atualizar
          </button>
        </div>
      </motion.header>

      {/* KPI Cards */}
      <motion.section
        className="relatorios-kpis"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-blue">
            <FiUsers />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total de Leads</span>
            <span className="kpi-value">{metrics.totalLeads}</span>
            <span className="kpi-change positive">
              <FiTrendingUp /> {dashboardData?.team_metrics?.leads_today || 0} hoje
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-green">
            <FiShoppingCart />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Vendas Realizadas</span>
            <span className="kpi-value">{metrics.wonDeals}</span>
            <span className="kpi-change positive">
              <FiCheckCircle /> {metrics.convertedLeads} leads convertidos
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-purple">
            <FiDollarSign />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Receita Total</span>
            <span className="kpi-value">{formatCurrency(metrics.totalRevenue)}</span>
            <span className="kpi-change neutral">
              <FiActivity /> Ticket médio: {formatCurrency(metrics.avgDealValue)}
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-orange">
            <FiTarget />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Taxa de Conversão</span>
            <span className="kpi-value">{formatPercent(metrics.conversionRate)}</span>
            <span className="kpi-change neutral">
              <FiClock /> {metrics.activeLeads} leads ativos
            </span>
          </div>
        </div>
      </motion.section>

      {/* Second Row KPIs - Financial */}
      {salesMetrics && (
        <motion.section
          className="relatorios-kpis relatorios-kpis-secondary"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <div className="kpi-card kpi-card-small">
            <div className="kpi-icon kpi-icon-green kpi-icon-small">
              <FiTrendingUp />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Receitas Extras</span>
              <span className="kpi-value kpi-value-small">{formatCurrency(salesMetrics.total_revenue || 0)}</span>
            </div>
          </div>

          <div className="kpi-card kpi-card-small">
            <div className="kpi-icon kpi-icon-red kpi-icon-small">
              <FiTrendingDown />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Despesas</span>
              <span className="kpi-value kpi-value-small">{formatCurrency(metrics.totalExpenses)}</span>
            </div>
          </div>

          <div className="kpi-card kpi-card-small">
            <div className={`kpi-icon kpi-icon-small ${metrics.profit >= 0 ? 'kpi-icon-green' : 'kpi-icon-red'}`}>
              <FiPercent />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">Lucro Líquido</span>
              <span className={`kpi-value kpi-value-small ${metrics.profit >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
                {formatCurrency(metrics.profit)}
              </span>
            </div>
          </div>
        </motion.section>
      )}

      {/* Charts Grid */}
      <div className="relatorios-grid">
        {/* Revenue Evolution Chart - NEW */}
        <motion.div
          className="relatorios-card relatorios-card-full"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="card-header">
            <h2>Evolução da Receita</h2>
            <span className="card-subtitle">Vendas, receitas e despesas ao longo do tempo</span>
          </div>
          <div className="chart-container">
            {revenueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={revenueChartData}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="date" stroke="var(--text-secondary)" />
                  <YAxis
                    yAxisId="left"
                    stroke="var(--text-secondary)"
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="var(--text-secondary)"
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                    }}
                    formatter={(value, name) => [formatCurrency(value),
                      name === 'sales' ? 'Vendas' :
                      name === 'expenses' ? 'Despesas' :
                      name === 'cumulative' ? 'Acumulado' : name
                    ]}
                  />
                  <Legend
                    formatter={(value) =>
                      value === 'sales' ? 'Vendas' :
                      value === 'expenses' ? 'Despesas' :
                      value === 'cumulative' ? 'Acumulado' : value
                    }
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="sales"
                    name="sales"
                    stroke="#22c55e"
                    fillOpacity={1}
                    fill="url(#colorSales)"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="expenses"
                    name="expenses"
                    fill="#ef4444"
                    radius={[4, 4, 0, 0]}
                    opacity={0.8}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumulative"
                    name="cumulative"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">
                <FiDollarSign />
                <p>Nenhuma venda registrada ainda</p>
                <p className="empty-chart-hint">Registre vendas nos leads para ver a evolução da receita</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Funil de Vendas */}
        <motion.div
          className="relatorios-card relatorios-card-large"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <div className="card-header">
            <h2>Funil de Vendas</h2>
            <span className="card-subtitle">Leads por etapa do funil</span>
          </div>
          <div className="chart-container">
            {funnelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis type="number" stroke="var(--text-secondary)" />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="var(--text-secondary)"
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                    }}
                    formatter={(value, name) => [
                      name === 'leads' ? `${value} leads` : formatCurrency(value),
                      name === 'leads' ? 'Quantidade' : 'Valor'
                    ]}
                  />
                  <Bar dataKey="leads" fill="var(--fastteam-primary)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">
                <FiActivity />
                <p>Nenhum dado disponível</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Leads por Fonte */}
        <motion.div
          className="relatorios-card"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <div className="card-header">
            <h2>Leads por Fonte</h2>
            <span className="card-subtitle">Origem dos seus leads</span>
          </div>
          <div className="chart-container">
            {sourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                    }}
                    formatter={(value) => [`${value} leads`, 'Quantidade']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">
                <FiActivity />
                <p>Nenhum dado disponível</p>
              </div>
            )}
            <div className="pie-legend">
              {sourceData.map((item, index) => (
                <div key={index} className="legend-item">
                  <span className="legend-dot" style={{ background: item.color }}></span>
                  <span className="legend-label">{item.name}</span>
                  <span className="legend-value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Evolução de Leads */}
        <motion.div
          className="relatorios-card relatorios-card-full"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35 }}
        >
          <div className="card-header">
            <h2>Evolução de Leads</h2>
            <span className="card-subtitle">Novos leads vs convertidos</span>
          </div>
          <div className="chart-container">
            {timeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={timeData}>
                  <defs>
                    <linearGradient id="colorNovos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--fastteam-primary)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--fastteam-primary)" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorConvertidos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="date" stroke="var(--text-secondary)" />
                  <YAxis stroke="var(--text-secondary)" />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="novos"
                    name="Novos Leads"
                    stroke="var(--fastteam-primary)"
                    fillOpacity={1}
                    fill="url(#colorNovos)"
                  />
                  <Area
                    type="monotone"
                    dataKey="convertidos"
                    name="Convertidos"
                    stroke="#3b82f6"
                    fillOpacity={1}
                    fill="url(#colorConvertidos)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">
                <FiActivity />
                <p>Nenhum dado disponível</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Métricas do Dashboard */}
        <motion.div
          className="relatorios-card"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <div className="card-header">
            <h2>Resumo da Operação</h2>
            <span className="card-subtitle">Integrações e automações</span>
          </div>
          <div className="summary-list">
            <div className="summary-item">
              <div className="summary-icon">
                <FiMessageCircle />
              </div>
              <div className="summary-content">
                <span className="summary-label">Integrações Ativas</span>
                <span className="summary-value">
                  {dashboardData?.integrations?.total_configured || 0}
                </span>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <FiActivity />
              </div>
              <div className="summary-content">
                <span className="summary-label">Fluxos Ativos</span>
                <span className="summary-value">
                  {dashboardData?.automations?.total_active || 0}
                </span>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <FiUsers />
              </div>
              <div className="summary-content">
                <span className="summary-label">Leads Hoje</span>
                <span className="summary-value">
                  {dashboardData?.team_metrics?.leads_today || 0}
                </span>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <FiCheckCircle />
              </div>
              <div className="summary-content">
                <span className="summary-label">Conversões Hoje</span>
                <span className="summary-value">
                  {dashboardData?.team_metrics?.conversions_today || 0}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Performance por Estágio */}
        <motion.div
          className="relatorios-card"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.45 }}
        >
          <div className="card-header">
            <h2>Performance do Funil</h2>
            <span className="card-subtitle">Valor por etapa</span>
          </div>
          <div className="funnel-stages">
            {funnelData.map((stage, index) => {
              const maxLeads = Math.max(...funnelData.map(s => s.leads), 1);
              const percentage = (stage.leads / maxLeads) * 100;

              return (
                <div key={index} className="funnel-stage">
                  <div className="funnel-stage-header">
                    <span className="funnel-stage-name">{stage.name}</span>
                    <span className="funnel-stage-count">{stage.leads} leads</span>
                  </div>
                  <div className="funnel-bar-track">
                    <div
                      className="funnel-bar-fill"
                      style={{
                        width: `${percentage}%`,
                        background: stage.color || 'var(--fastteam-primary)',
                      }}
                    />
                  </div>
                  <span className="funnel-stage-value">{formatCurrency(stage.value)}</span>
                </div>
              );
            })}
            {funnelData.length === 0 && (
              <div className="empty-funnel">
                <p>Configure as etapas do seu funil no CRM</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Relatorios;
